'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Check, Zap, Users, Star, AlertTriangle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { createClient } from '@/lib/supabase/client'
import { REFERENCE_PRICE, formatCreditCount } from '@/lib/pricing-display'
import { FREE_LIMITS } from '@/lib/limits'
import { FREE_PLAN, FIXED_COSTS, PER_MINUTE_COSTS, PRO_OPTIMAL, PLAN_CREDITS, PRO_PERIOD_DAYS } from '@/lib/credit-costs'

const PLANS = [
  {
    id: 'free',
    name: 'Gratuit',
    price: '0 €',
    period: '',
    description: 'Pour découvrir la plateforme',
    credits: FREE_PLAN.initialCredits,
    icon: Zap,
    // SOURCE UNIQUE des limites : `FREE_LIMITS` (src/lib/limits.ts), celle-là
    // même qui les fait respecter côté produit — comme le fait déjà UserPanel.
    // Écrites en dur, ces promesses avaient dérivé : « 3 codifications » alors
    // que la limite est de 5, et l'unité est l'ENTRETIEN, pas la codification
    // (`incrementLimit('codification_count', files.length)` compte les fichiers,
    // pas les lancements — on peut coder 5 entretiens en un seul run).
    features: [
      `${FREE_PLAN.initialCredits} crédits offerts`,
      `${FREE_LIMITS.transcription_count} transcription audio`,
      `${FREE_LIMITS.codification_count} entretiens à codifier`,
      // « Export Word (DOCX) » seul sous-estimait : la transcription produit du
      // .txt ET du .docx, la codification du .xlsx. Inventaire des boutons.
      'Export Word, Excel et TXT',
      // « Pas de sauvegarde des documents » était FAUX, et contredisait depuis
      // peu la FAQ de l'accueil, qui promet un historique conservé. La
      // sauvegarde ne dépend que de l'authentification — ni du plan, ni d'une
      // option. RLS par utilisateur sur `module_documents` et `transcriptions`,
      // et aucune surface d'administration ne lit leur contenu (seuls des
      // compteurs agrégés sur `profiles`).
      'Historique privé, téléchargeable à tout moment',
    ],
    limitations: [
      // « Accès sans inscription » était l'affirmation inverse de la réalité au
      // moment qui compte : le dépôt est libre, le LANCEMENT demande un compte.
      'Inscription requise pour lancer un traitement',
      // Remplace « Support communautaire uniquement » : aucun palier de support
      // n'est implémenté — c'est la raison pour laquelle « Support prioritaire »
      // a été retiré des CGU côté Pro. Cette limite-ci, elle, est réelle.
      'Rapport, note technique et correction réservés au Pro',
    ],
    cta: 'Commencer gratuitement',
    ctaVariant: 'outline' as const,
    disabled: false,
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    // Prix public officiel — SOURCE UNIQUE : src/lib/pricing-display.ts.
    // Le montant réellement débité dans la devise locale (ex. 13 100 XOF)
    // n'apparaît QUE dans le Payment Hub, au moment du choix du moyen de paiement.
    price: REFERENCE_PRICE.amount,
    period: REFERENCE_PRICE.period,
    description: 'Pour les professionnels de la recherche',
    credits: 1300,
    icon: Star,
    // Carte Pro alignée sur D-025 et sur l'inventaire des exports. Ce qu'elle
    // affirmait et qui était FAUX :
    //   · « 1 300 crédits / mois » — il n'existe aucun prélèvement récurrent :
    //     une souscription = une période de 30 jours, crédits non reportés ;
    //   · « Transcriptions illimitées » et « Codifications illimitées » — rien
    //     n'est illimité, tout est borné par les 1 300 crédits. C'était la
    //     promesse la plus trompeuse des deux pages tarifaires ;
    //   · « Export Word (DOCX) » — incomplet : Excel et TXT existent aussi ;
    //   · « Support prioritaire » — aucun palier de support n'est implémenté,
    //     même motif que son retrait des CGU.
    features: [
      `${formatCreditCount(PLAN_CREDITS.pro)} crédits par souscription`,
      `Valables ${PRO_PERIOD_DAYS} jours, non reportés`,
      'Accès à tous les modules',
      'Fichiers audio jusqu’à 2 Go',
      'Export Word, Excel et TXT',
      'Historique privé, téléchargeable à tout moment',
    ],
    limitations: [],
    cta: 'Souscrire au plan Pro',
    ctaVariant: 'default' as const,
    disabled: false,
    popular: true,
  },
  {
    id: 'teams',
    name: 'Équipes',
    price: 'Sur devis',
    period: '',
    description: 'Pour les organisations et cabinets',
    credits: null,
    icon: Users,
    features: [
      'Crédits mutualisés',
      'Gestion des membres',
      'Tableau de bord équipe',
      'Intégration API',
      'Formation incluse',
      'Support dédié',
      'SLA garanti',
    ],
    limitations: [],
    cta: 'Nous contacter',
    ctaVariant: 'outline' as const,
    disabled: true,
    popular: false,
  },
]

/** Décimale à la française sans dépendre de la locale du navigateur (hydratation). */
const dec = (n: number) => String(Math.round(n * 100) / 100).replace('.', ',')

/**
 * Coûts en crédits du plan **Pro** — DÉRIVÉS de `src/lib/credit-costs.ts`, la
 * source qui débite réellement.
 *
 * Deux principes, tirés de l'erreur qu'ils corrigent :
 *
 * 1. `cost` peut être un NOMBRE (forfait) ou une CHAÎNE (tarif à la minute).
 *    La transcription et la correction se facturent à la durée : leur afficher
 *    un forfait était faux dans la NATURE même du coût, pas seulement dans le
 *    montant. Un chercheur ne peut pas budgéter à partir d'un chiffre qui ne
 *    correspond à aucune unité réelle.
 *
 * 2. Le tableau annonce les coûts **Pro**. Le plan gratuit suit une calibration
 *    DISTINCTE (100 crédits = une seule session sur un seul module) : mélanger
 *    les deux dans une colonne unique rendait le tableau indéchiffrable.
 */
const CREDIT_COSTS: { label: string; cost: string | number; description: string }[] = [
  {
    label: 'Transcription audio',
    cost: `${dec(PER_MINUTE_COSTS.transcription)} / min`,
    description: `Selon la durée de l'audio — environ ${Math.round(PER_MINUTE_COSTS.transcription * 60)} crédits pour 1 h`,
  },
  {
    label: 'Correction du verbatim',
    cost: `${dec(PER_MINUTE_COSTS.correction)} / min`,
    description: `Selon la durée du verbatim — environ ${Math.round(PER_MINUTE_COSTS.correction * 60)} crédits pour 1 h`,
  },
  {
    label: 'Codification thématique',
    cost: FIXED_COSTS.coding,
    description: `Par session — jusqu'à ${PRO_OPTIMAL.codingMaxEntretiens} entretiens pour le même coût`,
  },
  {
    label: 'Rapport d’analyse',
    cost: FIXED_COSTS.analysis,
    description: `Par rapport — jusqu'à ${PRO_OPTIMAL.analysisMaxEntretiens} entretiens pour le même coût`,
  },
  {
    label: 'Note technique',
    cost: FIXED_COSTS['note-tech'],
    description: 'Par note générée à partir d’un TDR',
  },
]

export default function PricingPage() {
  const router = useRouter()
  const [paymentStatus, setPaymentStatus] = useState<'failed' | 'error' | 'invalid' | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const status = params.get('payment')
    if (status === 'failed' || status === 'error' || status === 'invalid') {
      setPaymentStatus(status)
      window.history.replaceState({}, '', '/pricing')
    }
  }, [])

  const handleProSubscribe = () => {
    // Payment Hub : pays et moyen de paiement sont choisis SUR la page (multi-pays),
    // et l'email y est pré-rempli depuis /api/credits. Aucun paramètre d'URL n'est
    // donc nécessaire — cela évite au passage de faire transiter email et nom dans
    // l'URL (historique de navigation, Referer, chemins analytics).
    router.push('/payment/hub')
  }

  const handlePlanAction = (planId: string) => {
    if (planId === 'free') {
      router.push('/')
    } else if (planId === 'pro') {
      handleProSubscribe()
    }
  }

  return (
    <div className="min-h-screen bg-tq-bg">
      {/* Header */}
      <header className="border-b border-tq-border bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/')}
                className="gap-2 text-tq-muted hover:text-tq-text"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <img src="/qualyse-logo.png" alt="Qualyse" className="h-8 w-8 rounded-lg" />
              <span className="font-bold text-tq-text">Qualyse</span>
            </div>
            <ThemeToggle className="h-9 w-9 text-tq-muted hover:text-tq-text hover:bg-tq-hover" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
        {/* Erreur de paiement */}
        {paymentStatus && (
          <div className="mb-10 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700 px-5 py-4">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                {paymentStatus === 'failed' ? 'Paiement non abouti' : 'Erreur lors du paiement'}
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-0.5">
                {paymentStatus === 'failed'
                  ? 'Le paiement n\'a pas été complété. Vous pouvez réessayer.'
                  : 'Une erreur technique est survenue. Veuillez réessayer ou contacter le support.'}
              </p>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold text-tq-text mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-lg text-tq-muted max-w-2xl mx-auto">
            Des crédits transparents pour chaque fonctionnalité. Commencez gratuitement, passez au Pro quand vous en avez besoin.
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PLANS.map((plan) => {
            const Icon = plan.icon
            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col p-6 border transition-shadow ${
                  plan.popular
                    ? 'border-tq-green shadow-lg shadow-tq-green/10 ring-1 ring-tq-green'
                    : 'border-tq-border'
                } bg-white dark:bg-slate-800`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-tq-green text-white border-0 px-3 py-0.5 text-xs font-semibold shadow">
                      ⭐ Populaire
                    </Badge>
                  </div>
                )}

                {/* Plan header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-2 rounded-lg ${plan.popular ? 'bg-tq-green-lighter dark:bg-tq-green/20' : 'bg-tq-border/50'}`}>
                      <Icon className={`h-4 w-4 ${plan.popular ? 'text-tq-green' : 'text-tq-muted'}`} />
                    </div>
                    <span className="font-bold text-tq-text">{plan.name}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold text-tq-text">{plan.price}</span>
                    {plan.period && <span className="text-tq-muted text-sm">{plan.period}</span>}
                  </div>
                  <p className="text-sm text-tq-muted">{plan.description}</p>
                  {plan.credits && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-tq-green">
                      <Zap className="h-3.5 w-3.5" />
                      {plan.credits.toLocaleString('fr-FR')} crédits
                      {plan.period ? ' / mois' : ' offerts'}
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-tq-text">
                      <Check className="h-4 w-4 text-tq-green mt-0.5 shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {plan.limitations.map((limitation) => (
                    <li key={limitation} className="flex items-start gap-2 text-sm text-tq-muted">
                      <span className="h-4 w-4 mt-0.5 shrink-0 text-center text-xs leading-4">✗</span>
                      {limitation}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  variant={plan.ctaVariant}
                  disabled={plan.disabled}
                  onClick={() => handlePlanAction(plan.id)}
                  className={`w-full ${
                    plan.popular
                      ? 'bg-tq-green hover:bg-tq-green/90 text-white border-0'
                      : ''
                  } ${plan.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {plan.cta}
                </Button>
                {plan.disabled && (
                  <p className="text-xs text-tq-muted text-center mt-2">Disponible prochainement</p>
                )}
              </Card>
            )
          })}
        </div>

        {/* Credit costs table */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-tq-text mb-2 text-center">Coûts indicatifs du plan Pro</h2>
          <p className="text-tq-muted text-center mb-8 text-sm">
            La codification, le rapport et la note technique sont des forfaits ;
            la transcription et la correction se facturent à la durée.
          </p>
          <div className="rounded-xl border border-tq-border overflow-hidden bg-white dark:bg-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tq-border bg-tq-bg dark:bg-slate-900">
                  <th className="text-left px-6 py-3 font-semibold text-tq-text">Fonctionnalité</th>
                  <th className="text-left px-6 py-3 font-semibold text-tq-text">Description</th>
                  <th className="text-right px-6 py-3 font-semibold text-tq-text">Crédits</th>
                </tr>
              </thead>
              <tbody>
                {CREDIT_COSTS.map((item, i) => (
                  <tr key={item.label} className={`border-b border-tq-border last:border-0 ${i % 2 === 0 ? '' : 'bg-tq-bg/40 dark:bg-slate-900/40'}`}>
                    <td className="px-6 py-3 font-medium text-tq-text">{item.label}</td>
                    <td className="px-6 py-3 text-tq-muted">{item.description}</td>
                    <td className="px-6 py-3 text-right">
                      <span className="inline-flex items-center gap-1 font-semibold text-tq-green">
                        <Zap className="h-3.5 w-3.5" />
                        {item.cost}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="text-center">
          <h2 className="text-xl font-bold text-tq-text mb-6">Questions fréquentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
            {[
              {
                // `proTopUpCredits` CONSERVE le solde d'un abonné Pro qui recharge
                // (base = wasPro ? currentCredits : 0, puis + 1300) : c'est un
                // avantage voulu. L'ancienne réponse affirmait l'inverse.
                q: 'Les crédits expirent-ils ?',
                a: 'Les crédits Pro sont conservés d’un mois à l’autre. Les crédits offerts aux nouveaux inscrits ne sont pas renouvelés.',
              },
              {
                // L'ancienne réponse — « accessible sans inscription » —
                // promettait l'inverse de ce qui se passe au moment utile :
                // `openAuthGate` s'ouvre au lancement dans les quatre modules.
                // Le parcours est conservé tel quel, la réponse le décrit.
                q: 'Puis-je utiliser la plateforme sans créer de compte ?',
                a: 'Vous pouvez parcourir la plateforme et déposer votre fichier sans compte. Lancer un traitement demande une inscription : la fenêtre s’ouvre à ce moment-là, et le fichier déjà déposé est conservé — le traitement repart tout seul après l’inscription. Les 100 crédits offerts sont attribués à la création du compte.',
              },
              {
                // Deux modes de facturation coexistent — l'ancienne réponse n'en
                // décrivait qu'un, et affirmait l'inverse pour l'autre. Les taux
                // sont dérivés de credit-costs.ts, comme le tableau ci-dessus.
                q: 'Comment sont comptabilisés les crédits ?',
                a: `Deux modes coexistent. La codification, le rapport d’analyse et la note technique consomment un forfait fixe par opération, quel que soit le nombre d’entretiens traités. La transcription et la correction sont facturées à la durée du contenu — environ ${dec(PER_MINUTE_COSTS.transcription)} et ${dec(PER_MINUTE_COSTS.correction)} crédit par minute en plan Pro.`,
              },
              {
                q: 'Puis-je changer de plan à tout moment ?',
                a: 'Oui. Vous pouvez passer au plan Pro à tout moment. La résiliation prend effet à la fin de la période en cours.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="rounded-lg border border-tq-border bg-white dark:bg-slate-800 p-4">
                <p className="font-semibold text-tq-text text-sm mb-1">{q}</p>
                <p className="text-sm text-tq-muted">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
